import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, LogOut, Edit2, Trash2, Star, Clock, Eye, MousePointerClick, AlertCircle, ShieldCheck, Building2, ExternalLink, Save, ChevronDown } from "lucide-react";
import CandidateResponsesPanel from "../components/CandidateResponsesPanel";
import { toast } from "sonner";

const ROLE_CATS = [
  "train-driver", "conductor", "depot-driver", "fitter", "engineering",
  "signalling", "customer-service", "graduate", "apprentice", "other",
];
const EMP_TYPES = ["full-time", "part-time", "contract", "apprenticeship"];
const TALENT_ROLE_OPTIONS = [
  { value: "train-driver", label: "Train driver" },
  { value: "conductor", label: "Conductor" },
  { value: "depot-driver", label: "Depot driver" },
  { value: "maintenance-rolling-stock", label: "Maintenance (Rolling Stock)" },
  { value: "maintenance-track-infrastructure", label: "Maintenance (Track or Infrastructure)" },
  { value: "administrative", label: "Administrative" },
  { value: "graduate", label: "Graduate" },
  { value: "signalling", label: "Signalling" },
  { value: "other", label: "Other" },
];

const TALENT_ROLE_LABELS = Object.fromEntries(TALENT_ROLE_OPTIONS.map((r) => [r.value, r.label]));
const prettyTalentRole = (value) => TALENT_ROLE_LABELS[value] || value || "—";
const STAGE_LABELS = {
  interest_registered: "Interest registered",
  email_verified: "Email verified",
  email_verified_pending_password: "Pending password setup",
  stage_one_passed: "Stage 1 — Basic Eligibility",
  stage_one_hold: "Stage 1 — Basic Eligibility (Hold)",
  stage_two_behavioural_profiling: "Stage 2 — Operational Behaviour Assessment",
  stage_two_target_role_selection: "Stage 2 — Operational Behaviour Assessment",
  stage_two: "Stage 2 — Operational Behaviour Assessment",
  stage_three_aptitude_tests: "Stage 3 — Scenario Aptitude Tests",
  stage_three: "Stage 3 — Scenario Aptitude Tests",
  stage_four_cv_submission: "Stage 4 — CV Submission",
  stage_five_simulated_interview: "Stage 5 — Simulated Interview",
  stage_six_recorded_interview: "Stage 6 — 1:1 Recorded Interview (Teams)",
};
const ADMIN_STATUS_LABELS = {
  new: "New",
  reviewing: "Reviewing",
  on_hold: "On hold",
  approved: "Approved",
  active: "Active",
  shortlist_ready: "Shortlist ready",
  paused: "Paused",
  rejected: "Rejected",
};
const formatStageLabel = (value) => STAGE_LABELS[String(value || "").toLowerCase()] || String(value || "—").replaceAll("_", " ");
const formatAdminStatusLabel = (value) => ADMIN_STATUS_LABELS[String(value || "").toLowerCase()] || String(value || "—").replaceAll("_", " ");
const stageBadgeClass = (stage) => {
  const key = String(stage || "").toLowerCase();
  if (key.includes("stage_two") || key.includes("stage_three") || key.includes("stage_four") || key.includes("stage_five") || key.includes("stage_six")) {
    return "bg-lime-100 text-lime-900 border-lime-300";
  }
  if (key.includes("hold")) return "bg-amber/20 text-ink border-amber/50";
  return "bg-sky/10 text-ink border-sky/30";
};
const adminBadgeClass = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "active" || key === "approved" || key === "shortlist_ready") return "bg-lime-100 text-lime-900 border-lime-300";
  if (key === "reviewing" || key === "new") return "bg-sky/10 text-ink border-sky/30";
  if (key === "on_hold" || key === "paused") return "bg-amber/20 text-ink border-amber/50";
  if (key === "rejected") return "bg-red-100 text-red-900 border-red-300";
  return "bg-paper text-ink border-border";
};

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const empty = {
  title: "", role_category: "train-driver", location: "", region: "",
  salary_min: "", salary_max: "", employment_type: "full-time",
  description: "", requirements: "", apply_url: "",
  start_date: today(), expiry_date: plusDays(30),
  expiry_reminder_days: 7,
};

function statusOf(v) {
  const t = today();
  if (v.status !== "live") return { label: v.status, tone: "neutral" };
  if (v.start_date && v.start_date > t) return { label: "Scheduled", tone: "scheduled" };
  if (v.expiry_date && v.expiry_date < t) return { label: "Expired", tone: "expired" };
  return { label: "Live", tone: "live" };
}

export default function EmployerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [vacancies, setVacancies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [talentPoolMembers, setTalentPoolMembers] = useState([]);
  const [talentPoolLoading, setTalentPoolLoading] = useState(false);
  const [viewingTalentMember, setViewingTalentMember] = useState(null);
  const [talentQ, setTalentQ] = useState("");
  const [talentRole, setTalentRole] = useState("");
  const [talentLocation, setTalentLocation] = useState("");
  const [talentRecruiterPostcode, setTalentRecruiterPostcode] = useState("");
  const [talentMaxDistanceKm, setTalentMaxDistanceKm] = useState("80");

  const TALENT_DISTANCE_PRESETS = [25, 50, 80, 120];

  const [inappThreads, setInappThreads] = useState([]);
  const [inappArchivedThreads, setInappArchivedThreads] = useState([]);
  const [inappActiveThread, setInappActiveThread] = useState(null);
  const [inappMessages, setInappMessages] = useState([]);
  const [inappReplyBody, setInappReplyBody] = useState("");
  const [inappReplySending, setInappReplySending] = useState(false);

  // Company profile editor state
  const [profile, setProfile] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  // Stripe — Feature this vacancy
  const [featuringId, setFeaturingId] = useState(null);

  const featureVacancy = async (vid) => {
    if (!window.confirm("Feature this vacancy for £50 (one-off)? You'll be redirected to Stripe Checkout. The vacancy is auto-pinned for 30 days the moment payment completes.")) return;
    setFeaturingId(vid);
    try {
      const r = await api.post("/checkout/payment-link", {
        package_id: "featured-vacancy",
        origin_url: window.location.origin,
        vacancy_id: vid,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setFeaturingId(null);
    }
  };

  const approved = user?.role === "admin" || user?.approved === true;
  const emailVerified = user?.role === "admin" || user?.email_verified === true;

  const fetch = async () => {
    setLoading(true);
    const [vr, sr, pr, inappOpen, inappArchived] = await Promise.all([
      api.get("/employer/vacancies"),
      api.get("/employer/stats"),
      api.get("/employer/profile").catch(() => ({ data: null })),
      api.get("/inapp/threads", { params: { archived: false, limit: 300 } }).catch(() => ({ data: [] })),
      api.get("/inapp/threads", { params: { archived: true, limit: 300 } }).catch(() => ({ data: [] })),
    ]);
    setVacancies(vr.data);
    setStats(sr.data);
    setProfile(pr.data);
    setInappThreads(inappOpen.data || []);
    setInappArchivedThreads(inappArchived.data || []);
    setLoading(false);
  };
  useEffect(() => { fetch(); }, []);

  const loadTalentPool = async () => {
    if (!approved || !emailVerified) return;
    setTalentPoolLoading(true);
    try {
      const r = await api.get("/employer/talent-pool/members", {
        params: {
          q: talentQ || undefined,
          role: talentRole || undefined,
          location: talentLocation || undefined,
          recruiter_postcode: talentRecruiterPostcode || undefined,
          max_distance_km: talentRecruiterPostcode ? (parseInt(talentMaxDistanceKm, 10) || 80) : undefined,
          limit: 80,
        },
      });
      setTalentPoolMembers(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load talent pool");
    } finally {
      setTalentPoolLoading(false);
    }
  };

  useEffect(() => {
    if (approved && emailVerified) {
      loadTalentPool();
    }
  }, [approved, emailVerified]);

  const resendVerification = async () => {
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      toast.success("Verification link re-sent. Check your inbox.");
    } catch { toast.error("Couldn't re-send right now."); }
  };

  const handleLogout = () => { logout(); nav("/"); };
  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (v) => {
    setEditing(v.id);
    setForm({
      title: v.title, role_category: v.role_category, location: v.location, region: v.region || "",
      salary_min: v.salary_min || "", salary_max: v.salary_max || "", employment_type: v.employment_type,
      description: v.description, requirements: v.requirements || "", apply_url: v.apply_url || "",
      start_date: v.start_date || today(),
      expiry_date: v.expiry_date || plusDays(30),
      expiry_reminder_days: v.expiry_reminder_days ?? 7,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.expiry_date) { toast.error("Please pick an expiry date — this is when the role auto-comes-down."); return; }
    if (form.start_date && form.expiry_date < form.start_date) { toast.error("Expiry must be on or after start date."); return; }
    const payload = {
      ...form,
      salary_min: form.salary_min ? parseInt(form.salary_min) : null,
      salary_max: form.salary_max ? parseInt(form.salary_max) : null,
      expiry_reminder_days: parseInt(form.expiry_reminder_days) || 0,
      featured: false,
    };
    try {
      if (editing) {
        await api.put(`/employer/vacancies/${editing}`, payload);
        toast.success("Vacancy updated");
      } else {
        await api.post("/employer/vacancies", payload);
        toast.success("Vacancy posted");
      }
      setOpen(false); fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this vacancy?")) return;
    await api.delete(`/employer/vacancies/${id}`);
    toast.success("Deleted");
    fetch();
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const payload = {
        profile_tagline: profile.profile_tagline || null,
        profile_about: profile.profile_about || null,
        profile_logo_url: profile.profile_logo_url || null,
        profile_hero_image_url: profile.profile_hero_image_url || null,
        profile_website: profile.profile_website || null,
        profile_careers_url: profile.profile_careers_url || null,
        profile_published: !!profile.profile_published,
      };
      const r = await api.put("/employer/profile", payload);
      setProfile(r.data);
      toast.success(payload.profile_published ? "Company page published — visible to the public." : "Company page saved as draft.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setProfileSaving(false);
    }
  };

  const messageTalentMember = async (member) => {
    const subject = window.prompt(`Subject for ${member.contact_name || member.email}`);
    if (!subject) return;
    const message = window.prompt("Message body");
    if (!message) return;
    try {
      await api.post(`/employer/talent-pool/members/${member.id}/message`, { subject, message });
      toast.success("Message sent");
      loadTalentPool();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message");
    }
  };

  const loadInappThread = async (threadId) => {
    try {
      const r = await api.get(`/inapp/threads/${threadId}/messages`);
      setInappActiveThread(r.data.thread);
      setInappMessages(r.data.messages || []);
      await api.post(`/inapp/threads/${threadId}/read`, {});
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load notification thread");
    }
  };

  const replyInappThread = async () => {
    if (!inappActiveThread?.id) return;
    if (!inappReplyBody.trim()) { toast.error("Reply cannot be empty"); return; }
    setInappReplySending(true);
    try {
      await api.post(`/inapp/threads/${inappActiveThread.id}/reply`, { body: inappReplyBody, format: "text" });
      setInappReplyBody("");
      await loadInappThread(inappActiveThread.id);
      toast.success("Reply sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reply failed");
    } finally {
      setInappReplySending(false);
    }
  };

  const archiveInappThread = async (threadId, archived = true) => {
    try {
      await api.post(`/inapp/threads/${threadId}/archive`, { archived });
      if (inappActiveThread?.id === threadId) {
        setInappActiveThread(null);
        setInappMessages([]);
      }
      fetch();
      toast.success(archived ? "Archived" : "Moved to inbox");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update archive state");
    }
  };

  const STAT_CARDS = stats ? [
    { l: "Live now", v: stats.live_count, i: ShieldCheck },
    { l: "Scheduled", v: stats.scheduled_count, i: Clock },
    { l: "Expired", v: stats.expired_count, i: AlertCircle },
    { l: "Total views", v: stats.total_views, i: Eye },
    { l: "Apply clicks", v: stats.total_clicks, i: MousePointerClick },
    { l: "Click rate", v: `${stats.apply_click_rate_pct}%`, i: Star },
  ] : [];
  const unreadInappCount = (inappThreads || []).filter((t) => !t.recipient_read_at).length;

  return (
    <div className="min-h-screen bg-sand">
      <header className="bg-navy text-bone">
        <div className="livery-stripe h-1" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Railway Careers UK" className="w-9 h-9 rounded-full bg-cream" />
            <div className="font-display font-bold tracking-tight text-sm">Employer Portal</div>
          </Link>
          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <div className="text-xs text-bone/60">Signed in as</div>
              <div className="text-sm font-semibold">{user?.company_name}</div>
              <div className="text-xs text-bone/70 mt-0.5">{unreadInappCount} unread notifications</div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-bone/30 text-bone hover:bg-cream/10" data-testid="emp-logout">
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {!emailVerified && (
          <div className="mb-6 rounded-lg border-2 border-amber bg-amber/10 p-5 flex items-start gap-4 flex-wrap" data-testid="emp-verify-banner">
            <AlertCircle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[260px]">
              <div className="font-display font-bold text-ink text-lg">Confirm your email address</div>
              <p className="text-sm text-ink/80 mt-1 max-w-2xl">
                We sent a confirmation link to <strong>{user?.email}</strong>. Please click it
                to verify your address — admin can only approve confirmed accounts. Link valid for 72 hours.
              </p>
            </div>
            <Button variant="outline" onClick={resendVerification} className="border-ink/30 text-ink" data-testid="emp-verify-resend">
              Re-send link
            </Button>
          </div>
        )}
        {emailVerified && !approved && (
          <div className="mb-8 rounded-lg border-2 border-amber bg-amber/10 p-5 flex items-start gap-4" data-testid="emp-pending-banner">
            <Clock className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-display font-bold text-ink text-lg">Account awaiting approval</div>
              <p className="text-sm text-ink/80 mt-1 max-w-2xl">
                Thanks for confirming, {user?.contact_name?.split(" ")[0] || "team"}. To stop spurious or fraudulent listings,
                every new employer is manually verified before they can post. We'll email you once you're approved
                — usually within a working day. Need it sooner? Drop a line to <a href="mailto:employer.accounts@railwaycareers.co.uk" className="underline font-semibold">employer.accounts@railwaycareers.co.uk</a>.
              </p>
            </div>
          </div>
        )}

        <div className="mb-8 grid lg:grid-cols-2 gap-6" data-testid="emp-inapp-section">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border bg-sand text-xs uppercase tracking-wider text-muted-foreground">Inbox notifications</div>
            <div className="max-h-[260px] overflow-auto">
              {inappThreads.length === 0 ? (
                <div className="p-5 text-sm text-muted-foreground">No notifications in your inbox.</div>
              ) : inappThreads.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => loadInappThread(t.id)}
                  className="w-full text-left px-4 py-3 border-b border-border hover:bg-paper/40"
                  data-testid={`emp-inapp-thread-${t.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-ink truncate">{t.subject}</div>
                    {!t.recipient_read_at && <span className="text-[10px] px-2 py-0.5 rounded bg-amber/30 text-ink font-semibold">Unread</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">{t.last_message_preview || "—"}</div>
                </button>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-border bg-paper/30">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Archive</div>
              <div className="mt-2 max-h-[120px] overflow-auto space-y-2">
                {inappArchivedThreads.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No archived notifications.</div>
                ) : inappArchivedThreads.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-muted-foreground truncate">{t.subject}</span>
                    <Button size="sm" variant="outline" onClick={() => archiveInappThread(t.id, false)} data-testid={`emp-inapp-unarchive-${t.id}`}>Restore</Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            {!inappActiveThread ? (
              <div className="text-sm text-muted-foreground">Select a notification thread to read and reply.</div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-bold text-ink text-lg">{inappActiveThread.subject}</div>
                    <div className="text-xs text-muted-foreground mt-1">Updated: {(inappActiveThread.updated_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => archiveInappThread(inappActiveThread.id, true)} data-testid="emp-inapp-archive">Archive</Button>
                </div>
                <div className="mt-3 max-h-[280px] overflow-auto border border-border rounded-lg bg-background p-3 space-y-3">
                  {inappMessages.map((m) => (
                    <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.sender_role === "admin" ? "bg-sky/10 border border-sky/30" : "bg-paper border border-border"}`}>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{m.sender_role === "admin" ? "Admin" : "You"}</div>
                      <div className="mt-1 whitespace-pre-wrap">{m.body_text || ""}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <Label>Reply</Label>
                  <Textarea className="mt-1 min-h-[90px]" value={inappReplyBody} onChange={(e) => setInappReplyBody(e.target.value)} data-testid="emp-inapp-reply" />
                  <div className="mt-2 flex gap-2">
                    <Button onClick={replyInappThread} disabled={inappReplySending} className="bg-amber text-ink hover:bg-amber-600" data-testid="emp-inapp-reply-send">{inappReplySending ? "Sending..." : "Send reply"}</Button>
                    <Button variant="outline" onClick={() => loadInappThread(inappActiveThread.id)} data-testid="emp-inapp-refresh">Refresh</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-ink font-semibold">Your vacancies</div>
            <h1 className="font-display font-extrabold text-ink text-3xl md:text-4xl tracking-tight mt-1">Manage live listings</h1>
          </div>
          <Button
            onClick={startNew}
            disabled={!approved || !emailVerified}
            className="bg-amber text-ink hover:bg-amber-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="emp-new-vacancy"
            title={!emailVerified ? "Confirm your email first" : approved ? "" : "Awaiting admin approval"}
          >
            <Plus className="w-4 h-4 mr-2" /> New vacancy
          </Button>
        </div>

        {/* Company Page editor */}
        {profile && (
          <div className="mt-8 bg-card border border-border rounded-lg overflow-hidden" data-testid="company-page-card">
            <button
              type="button"
              onClick={() => setProfileOpen(!profileOpen)}
              className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-sand/50 transition-colors"
              data-testid="company-page-toggle"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-md bg-amber/20 inline-flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-amber-700" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-ink text-lg tracking-tight">Your Company Page</div>
                  <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-2 mt-0.5">
                    <span>
                      Status:{" "}
                      {profile.profile_published ? (
                        <span className="inline-flex items-center gap-1 text-lime-700 font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-lime-600" /> Published
                        </span>
                      ) : (
                        <span className="text-muted-foreground font-semibold">Draft</span>
                      )}
                    </span>
                    {profile.featured_employer && (
                      <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                        <Star className="w-3 h-3 fill-amber" /> Featured
                      </span>
                    )}
                    <span className="text-muted-foreground">·</span>
                    <Link
                      to={`/companies/${profile.profile_slug}`}
                      target="_blank"
                      className="text-ink hover:text-amber-700 font-mono text-xs underline inline-flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="company-page-preview"
                    >
                      /companies/{profile.profile_slug} <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform shrink-0 ${profileOpen ? "rotate-180" : ""}`} />
            </button>

            {profileOpen && (
              <form onSubmit={saveProfile} className="px-5 md:px-6 pb-6 border-t border-border pt-5 space-y-4" data-testid="company-page-form">
                <div>
                  <Label>Headline tagline <span className="text-[11px] text-muted-foreground">— one-line summary, shown under company name</span></Label>
                  <Input
                    className="mt-1"
                    maxLength={180}
                    placeholder="e.g. Powering Britain's railway with 35,000 colleagues across infrastructure, operations and engineering."
                    value={profile.profile_tagline || ""}
                    onChange={(e) => setProfile({ ...profile, profile_tagline: e.target.value })}
                    data-testid="profile-tagline"
                  />
                </div>
                <div>
                  <Label>About us <span className="text-[11px] text-muted-foreground">— up to 4,000 chars, markdown-friendly, shown in sidebar</span></Label>
                  <Textarea
                    className="mt-1 min-h-[140px]"
                    maxLength={4000}
                    placeholder="Tell applicants who you are, what makes your depots a great place to work, the kind of people who thrive here, and anything that distinguishes your recruitment or training offer."
                    value={profile.profile_about || ""}
                    onChange={(e) => setProfile({ ...profile, profile_about: e.target.value })}
                    data-testid="profile-about"
                  />
                  <div className="text-[11px] text-muted-foreground mt-1 text-right">{(profile.profile_about || "").length} / 4000</div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Logo URL <span className="text-[11px] text-muted-foreground">(leave blank for auto-favicon)</span></Label>
                    <Input type="url" className="mt-1" placeholder="https://…/logo.png"
                      value={profile.profile_logo_url || ""}
                      onChange={(e) => setProfile({ ...profile, profile_logo_url: e.target.value })}
                      data-testid="profile-logo" />
                  </div>
                  <div>
                    <Label>Hero image URL <span className="text-[11px] text-muted-foreground">(optional — full-width header)</span></Label>
                    <Input type="url" className="mt-1" placeholder="https://…/depot-hero.jpg"
                      value={profile.profile_hero_image_url || ""}
                      onChange={(e) => setProfile({ ...profile, profile_hero_image_url: e.target.value })}
                      data-testid="profile-hero" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Company website</Label>
                    <Input type="url" className="mt-1" placeholder="https://…"
                      value={profile.profile_website || ""}
                      onChange={(e) => setProfile({ ...profile, profile_website: e.target.value })}
                      data-testid="profile-website" />
                  </div>
                  <div>
                    <Label>Main careers page</Label>
                    <Input type="url" className="mt-1" placeholder="https://yourcompany.co.uk/careers"
                      value={profile.profile_careers_url || ""}
                      onChange={(e) => setProfile({ ...profile, profile_careers_url: e.target.value })}
                      data-testid="profile-careers-url" />
                  </div>
                </div>
                <label className={`flex items-center gap-2 text-sm select-none border-t border-border pt-4 ${user?.approved || user?.role === "admin" ? "cursor-pointer" : "opacity-70 cursor-not-allowed"}`} data-testid="profile-publish-toggle">
                  <input
                    type="checkbox"
                    checked={!!profile.profile_published}
                    disabled={!user?.approved && user?.role !== "admin"}
                    onChange={(e) => setProfile({ ...profile, profile_published: e.target.checked })}
                    data-testid="profile-publish-checkbox"
                  />
                  <span>
                    <strong className="text-ink">Publish my company page</strong>
                    <span className="text-muted-foreground"> — make it visible to the public at <code className="font-mono text-[12px] text-ink">/companies/{profile.profile_slug}</code>.</span>
                    {!user?.approved && user?.role !== "admin" && (
                      <span className="block text-[11px] text-amber-800 mt-1" data-testid="publish-blocked-note">
                        Awaiting admin approval — you&apos;ll be able to publish the moment your account is approved.
                      </span>
                    )}
                  </span>
                </label>
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Button type="submit" disabled={profileSaving} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="profile-save">
                    <Save className="w-4 h-4 mr-2" />
                    {profileSaving ? "Saving…" : profile.profile_published ? "Save & publish" : "Save as draft"}
                  </Button>
                  {profile.profile_published && (
                    <Button asChild type="button" variant="outline">
                      <Link to={`/companies/${profile.profile_slug}`} target="_blank" rel="noopener noreferrer">
                        View live page <ExternalLink className="ml-2 w-3.5 h-3.5" />
                      </Link>
                    </Button>
                  )}
                </div>
              </form>
            )}
          </div>
        )}

        {/* Talent pool access */}
        {approved && emailVerified ? (
          <div className="mt-8 bg-card border border-border rounded-lg p-5" data-testid="emp-talent-pool-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-ink font-semibold">Talent Pool</div>
                <h2 className="font-display font-bold text-ink text-xl mt-1">Verified Employer Candidate Search</h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                  Candidate profiles are visible only to verified, approved employers. Use role and postcode distance filters to shortlist suitable candidates quickly.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button asChild variant="outline" data-testid="emp-open-candidate-explorer">
                  <Link to="/talent-pool/candidates">Open candidate explorer</Link>
                </Button>
                <div className="inline-flex items-center gap-2 rounded-full border border-lime-300 bg-lime-50 px-3 py-1 text-xs font-semibold text-lime-800">
                  <span className="h-2 w-2 rounded-full bg-lime-500" />
                  Verified employer access
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-border bg-background p-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Search</Label>
                  <Input
                    className="mt-1"
                    placeholder="Name, role or location"
                    value={talentQ}
                    onChange={(e) => setTalentQ(e.target.value)}
                    data-testid="emp-talent-q"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Role</Label>
                  <select
                    className="mt-1 w-full rounded-md bg-background border border-border px-3 py-2"
                    value={talentRole}
                    onChange={(e) => setTalentRole(e.target.value)}
                    data-testid="emp-talent-role"
                  >
                    <option value="">All roles</option>
                    {TALENT_ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Preferred location</Label>
                  <Input
                    className="mt-1"
                    placeholder="City or region"
                    value={talentLocation}
                    onChange={(e) => setTalentLocation(e.target.value)}
                    data-testid="emp-talent-location"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Your postcode</Label>
                  <Input
                    className="mt-1 uppercase"
                    placeholder="M1 2AB"
                    value={talentRecruiterPostcode}
                    onChange={(e) => setTalentRecruiterPostcode(e.target.value)}
                    data-testid="emp-talent-postcode"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Max distance (km)</Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      placeholder="80"
                      type="number"
                      min={5}
                      max={400}
                      value={talentMaxDistanceKm}
                      onChange={(e) => setTalentMaxDistanceKm(e.target.value)}
                      data-testid="emp-talent-max-distance"
                    />
                    <Button
                      onClick={loadTalentPool}
                      className="bg-navy text-white hover:bg-lime-600 hover:text-slate-950 font-semibold"
                      data-testid="emp-talent-search"
                    >
                      Search
                    </Button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {TALENT_DISTANCE_PRESETS.map((d) => (
                      <button
                        key={d}
                        type="button"
                        className={`px-2 py-1 rounded-md border text-[11px] ${String(d) === String(talentMaxDistanceKm) ? "border-amber bg-amber/15 text-ink font-semibold" : "border-border text-muted-foreground hover:text-ink"}`}
                        onClick={() => setTalentMaxDistanceKm(String(d))}
                        data-testid={`emp-talent-distance-${d}`}
                      >
                        {d}km
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 border border-border rounded-lg overflow-x-auto">
              <div className="md:hidden divide-y divide-border">
                {talentPoolLoading && (
                  <div className="p-5 text-sm text-muted-foreground text-center">Loading talent pool…</div>
                )}
                {!talentPoolLoading && talentPoolMembers.length === 0 && (
                  <div className="p-5 text-sm text-muted-foreground text-center">No members match your filters yet.</div>
                )}
                {!talentPoolLoading && talentPoolMembers.map((m) => (
                  <div key={`mobile-${m.id}`} className="p-4 space-y-3" data-testid={`emp-talent-member-mobile-${m.id}`}>
                    <div>
                      <div className="font-semibold text-ink">{m.contact_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{m.email}</div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {(m.target_roles || []).length === 0 ? (
                        <span className="text-sm text-muted-foreground">—</span>
                      ) : (m.target_roles || []).map((role) => (
                        <span key={`mobile-${m.id}-${role}`} className="inline-flex items-center rounded-full bg-paper border border-border px-2 py-0.5 text-[11px] text-ink">
                          {prettyTalentRole(role)}
                        </span>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="uppercase tracking-wider text-muted-foreground">Location</div>
                        <div className="text-ink mt-0.5">{m.preferred_locations || "—"}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-muted-foreground">Postcode</div>
                        <div className="text-ink font-mono mt-0.5">{m.preferred_postcode || "—"}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-muted-foreground">Distance</div>
                        <div className="mt-0.5">
                          {Number.isFinite(m.distance_km)
                            ? <span className="inline-flex items-center rounded-full border border-lime-300 bg-lime-100 px-2 py-0.5 text-[11px] font-semibold text-lime-900">{m.distance_km} km</span>
                            : talentRecruiterPostcode
                              ? <span className="inline-flex items-center rounded-full border border-amber/50 bg-amber/20 px-2 py-0.5 text-[11px] font-semibold text-ink">Out of range</span>
                              : <span className="text-muted-foreground">—</span>}
                        </div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-muted-foreground">Stage 1 score</div>
                        <div className="text-ink mt-0.5">{m.gateway_scores?.overall_stage_one_pct ?? "—"}%</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-muted-foreground">Stage 2 outcome</div>
                        <div className="text-ink mt-0.5">{(m.stage_two_profile?.progression_outcome || "-").replaceAll("_", " ")}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-muted-foreground">Stage 2 safety</div>
                        <div className="text-ink mt-0.5">{m.stage_two_profile?.overall_safety_pct ?? "—"}%</div>
                      </div>
                      <div className="col-span-2">
                        <div className="uppercase tracking-wider text-muted-foreground">Stage 2 reason</div>
                        <div className="text-ink mt-0.5">{m.stage_two_profile?.outcome_reason_primary || "-"}</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${stageBadgeClass(m.talent_pool_stage)}`}>
                        {formatStageLabel(m.talent_pool_stage)}
                      </span>
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${adminBadgeClass(m.talent_pool_admin_status)}`}>
                        Admin: {formatAdminStatusLabel(m.talent_pool_admin_status)}
                      </span>
                    </div>

                    <div className="pt-1 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setViewingTalentMember(m)}
                        className="flex-1"
                        data-testid={`emp-talent-view-mobile-${m.id}`}
                      >
                        View responses
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => messageTalentMember(m)}
                        className="flex-1 bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950 font-semibold"
                        data-testid={`emp-talent-message-mobile-${m.id}`}
                      >
                        Message
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <table className="hidden md:table w-full text-sm">
                <thead className="bg-sand">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Member</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Roles</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Location / postcode</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Distance</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Gateway scores</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-ink font-semibold">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {talentPoolLoading && (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">Loading talent pool…</td>
                    </tr>
                  )}
                  {!talentPoolLoading && talentPoolMembers.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">No members match your filters yet.</td>
                    </tr>
                  )}
                  {!talentPoolLoading && talentPoolMembers.map((m) => (
                    <tr key={m.id} className="border-t border-border hover:bg-paper/40" data-testid={`emp-talent-member-${m.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-ink">{m.contact_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{m.email}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div className="flex flex-wrap gap-1.5">
                          {(m.target_roles || []).length === 0 ? (
                            <span>—</span>
                          ) : (m.target_roles || []).map((role) => (
                            <span key={`${m.id}-${role}`} className="inline-flex items-center rounded-full bg-paper border border-border px-2 py-0.5 text-[11px] text-ink">
                              {prettyTalentRole(role)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div>{m.preferred_locations || "—"}</div>
                        <div className="text-xs font-mono mt-1">{m.preferred_postcode || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {Number.isFinite(m.distance_km)
                          ? <span className="inline-flex items-center rounded-full border border-lime-300 bg-lime-100 px-2 py-0.5 text-[11px] font-semibold text-lime-900">{m.distance_km} km</span>
                          : talentRecruiterPostcode
                            ? <span className="inline-flex items-center rounded-full border border-amber/50 bg-amber/20 px-2 py-0.5 text-[11px] font-semibold text-ink">Out of range</span>
                            : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div className="text-xs">Overall: <span className="font-semibold text-ink">{m.gateway_scores?.overall_stage_one_pct ?? "—"}%</span></div>
                        <div className="text-xs mt-1">Eligibility: {m.gateway_scores?.eligibility_readiness_pct ?? "—"}%</div>
                        <div className="text-xs">Compliance: {m.gateway_scores?.compliance_readiness_pct ?? "—"}%</div>
                        <div className="text-xs">Operational: {m.gateway_scores?.operational_readiness_pct ?? "—"}%</div>
                        <div className="text-xs">Motivation: {m.gateway_scores?.motivation_readiness_pct ?? "—"}%</div>
                        <div className="text-xs mt-2">Stage 2 outcome: <span className="text-ink font-semibold">{(m.stage_two_profile?.progression_outcome || "-").replaceAll("_", " ")}</span></div>
                        <div className="text-xs">Stage 2 safety: {m.stage_two_profile?.overall_safety_pct ?? "—"}%</div>
                        <div className="text-xs mt-1">Stage 2 reason: {m.stage_two_profile?.outcome_reason_primary || "-"}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground space-y-1">
                        <div className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${stageBadgeClass(m.talent_pool_stage)}`}>
                          {formatStageLabel(m.talent_pool_stage)}
                        </div>
                        <div className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${adminBadgeClass(m.talent_pool_admin_status)}`}>
                          Admin: {formatAdminStatusLabel(m.talent_pool_admin_status)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right space-y-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setViewingTalentMember(m)}
                          data-testid={`emp-talent-view-${m.id}`}
                        >
                          View responses
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => messageTalentMember(m)}
                          className="bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950 font-semibold"
                          data-testid={`emp-talent-message-${m.id}`}
                        >
                          Message
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <CandidateResponsesPanel
              member={viewingTalentMember}
              isAdmin={false}
              open={!!viewingTalentMember}
              onClose={() => setViewingTalentMember(null)}
              onMessage={(m) => messageTalentMember(m)}
            />
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-amber/40 bg-amber/10 p-5" data-testid="emp-talent-pool-locked">
            <div className="font-display font-bold text-ink text-lg">Talent Pool access restricted</div>
            <p className="text-sm text-ink/80 mt-2 max-w-3xl">
              The candidate talent pool is available only to verified, approved employer accounts. Complete email verification and await approval to unlock recruiter search tools.
            </p>
          </div>
        )}

        {/* Metrics */}

        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="emp-stats">
            {STAT_CARDS.map((s) => (
              <div key={s.l} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  <s.i className="w-3.5 h-3.5" /> {s.l}
                </div>
                <div className="font-mono font-bold text-ink text-2xl mt-2">{s.v ?? "—"}</div>
              </div>
            ))}
          </div>
        )}

        {stats?.next_expiry && (
          <div className="mt-4 text-xs text-muted-foreground font-mono">
            Next vacancy auto-comes-down on <span className="font-semibold text-ink">{stats.next_expiry}</span>.
          </div>
        )}

        {/* Top performers */}
        {stats && stats.top_vacancies?.length > 0 && stats.total_views > 0 && (
          <div className="mt-8 bg-card border border-border rounded-lg p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-ink font-semibold mb-3">Top performing vacancies</div>
            <div className="space-y-2.5">
              {stats.top_vacancies.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 text-sm border-b border-border last:border-0 pb-2 last:pb-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {t.featured && <Star className="w-3.5 h-3.5 text-amber fill-amber shrink-0" />}
                    <span className="font-semibold text-ink truncate">{t.title}</span>
                  </div>
                  <div className="flex items-center gap-4 font-mono text-xs text-muted-foreground shrink-0">
                    <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {t.views_count}</span>
                    <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {t.clicks_count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 bg-card border border-border rounded-lg overflow-hidden">
          {loading ? <div className="p-12 text-center text-muted-foreground">Loading…</div> : vacancies.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-muted-foreground">{approved ? "No vacancies yet. Post your first." : "Once approved, your posted roles will appear here."}</p>
              {approved && <Button onClick={startNew} className="mt-4 bg-navy text-bone hover:bg-lime-600 hover:text-ink">Post a vacancy</Button>}
            </div>
          ) : (
            <>
              <div className="md:hidden divide-y divide-border">
                {vacancies.map((v) => {
                  const s = statusOf(v);
                  const tone = {
                    live: "bg-lime-100 text-lime-900",
                    scheduled: "bg-sand text-ink",
                    expired: "bg-red-100 text-red-900",
                    neutral: "bg-sand text-muted-foreground",
                  }[s.tone];
                  return (
                    <div key={`mobile-vac-${v.id}`} className="p-4 space-y-3" data-testid={`emp-vac-card-${v.id}`}>
                      <div className="font-semibold text-ink flex items-start gap-2">
                        {v.featured && <Star className="w-4 h-4 text-amber fill-amber mt-0.5" />}
                        <span>{v.title}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><div className="uppercase tracking-wider text-muted-foreground">Role</div><div className="text-ink mt-0.5">{v.role_category}</div></div>
                        <div><div className="uppercase tracking-wider text-muted-foreground">Location</div><div className="text-ink mt-0.5">{v.location}</div></div>
                        <div><div className="uppercase tracking-wider text-muted-foreground">Goes live</div><div className="text-ink font-mono mt-0.5">{v.start_date || "Immediately"}</div></div>
                        <div><div className="uppercase tracking-wider text-muted-foreground">Comes down</div><div className="text-ink font-mono mt-0.5">{v.expiry_date || "—"}</div></div>
                        <div><div className="uppercase tracking-wider text-muted-foreground">Status</div><div className="mt-0.5"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${tone}`}>{s.label}</span></div></div>
                        <div><div className="uppercase tracking-wider text-muted-foreground">Views · Clicks</div><div className="text-ink font-mono mt-0.5">{v.views_count || 0} · {v.clicks_count || 0}</div></div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {!v.featured && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => featureVacancy(v.id)}
                            disabled={featuringId === v.id}
                            title="Feature this vacancy for £50 (30 days)"
                            data-testid={`emp-feature-mobile-${v.id}`}
                          >
                            <Star className="w-3.5 h-3.5 mr-1.5" /> Feature
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => startEdit(v)} data-testid={`emp-edit-mobile-${v.id}`}><Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
                        <Button size="sm" variant="outline" onClick={() => remove(v.id)} data-testid={`emp-delete-mobile-${v.id}`}><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete</Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <table className="hidden md:table w-full text-sm">
                <thead className="bg-sand">
                  <tr className="text-left">
                    <th className="px-5 py-3 font-semibold text-ink text-xs uppercase tracking-wider">Title</th>
                    <th className="px-5 py-3 font-semibold text-ink text-xs uppercase tracking-wider">Role</th>
                    <th className="px-5 py-3 font-semibold text-ink text-xs uppercase tracking-wider">Location</th>
                    <th className="px-5 py-3 font-semibold text-ink text-xs uppercase tracking-wider">Goes live</th>
                    <th className="px-5 py-3 font-semibold text-ink text-xs uppercase tracking-wider">Comes down</th>
                    <th className="px-5 py-3 font-semibold text-ink text-xs uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 font-semibold text-ink text-xs uppercase tracking-wider">Views</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {vacancies.map((v) => {
                    const s = statusOf(v);
                    const tone = {
                      live: "bg-lime-100 text-lime-900",
                      scheduled: "bg-sand text-ink",
                      expired: "bg-red-100 text-red-900",
                      neutral: "bg-sand text-muted-foreground",
                    }[s.tone];
                    return (
                      <tr key={v.id} className="border-t border-border" data-testid={`emp-vac-row-${v.id}`}>
                        <td className="px-5 py-4">
                          <div className="font-semibold text-ink flex items-center gap-2">
                            {v.featured && <Star className="w-3.5 h-3.5 text-amber fill-amber" />}
                            {v.title}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-muted-foreground">{v.role_category}</td>
                        <td className="px-5 py-4 text-muted-foreground">{v.location}</td>
                        <td className="px-5 py-4 text-muted-foreground font-mono text-xs">{v.start_date || "Immediately"}</td>
                        <td className="px-5 py-4 text-muted-foreground font-mono text-xs">{v.expiry_date || "—"}</td>
                        <td className="px-5 py-4"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${tone}`}>{s.label}</span></td>
                        <td className="px-5 py-4 text-muted-foreground font-mono text-xs">{v.views_count || 0} · {v.clicks_count || 0}</td>
                        <td className="px-5 py-4 text-right">
                          {!v.featured && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => featureVacancy(v.id)}
                              disabled={featuringId === v.id}
                              title="Feature this vacancy for £50 (30 days)"
                              data-testid={`emp-feature-${v.id}`}
                            >
                              <Star className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => startEdit(v)} data-testid={`emp-edit-${v.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(v.id)} data-testid={`emp-delete-${v.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit vacancy" : "Post a new vacancy"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="emp-vac-form">
            <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="vac-form-title" /></div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Role category</Label>
                <Select value={form.role_category} onValueChange={(v) => setForm({ ...form, role_category: v })}>
                  <SelectTrigger className="mt-1" data-testid="vac-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLE_CATS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Employment type</Label>
                <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{EMP_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Location</Label><Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" /></div>
              <div><Label>Region</Label><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="mt-1" placeholder="e.g. North West" /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Salary min (£)</Label><Input type="number" value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: e.target.value })} className="mt-1" /></div>
              <div><Label>Salary max (£)</Label><Input type="number" value={form.salary_max} onChange={(e) => setForm({ ...form, salary_max: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Goes live <span className="text-muted-foreground font-normal">(optional — defaults to now)</span></Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="vac-form-start" />
              </div>
              <div>
                <Label>Auto-comes-down on <span className="text-red-600">*</span></Label>
                <Input type="date" required value={form.expiry_date} min={form.start_date || today()} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1" data-testid="vac-form-expiry" />
              </div>
            </div>
            <div>
              <Label>Email me a reminder <span className="text-muted-foreground font-normal">days before expiry</span></Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={60}
                  value={form.expiry_reminder_days}
                  onChange={(e) => setForm({ ...form, expiry_reminder_days: e.target.value })}
                  className="w-28"
                  data-testid="vac-form-reminder-days"
                />
                <span className="text-xs text-muted-foreground">
                  {parseInt(form.expiry_reminder_days) === 0
                    ? "Reminders disabled — vacancy will simply auto-come-down on the expiry date."
                    : `We'll email you ${form.expiry_reminder_days} day(s) before expiry with a one-click extend link.`}
                </span>
              </div>
            </div>
            <div><Label>Description</Label><Textarea required className="mt-1 min-h-[120px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Requirements</Label><Textarea className="mt-1 min-h-[80px]" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} /></div>
            <div><Label>Apply URL</Label><Input type="url" value={form.apply_url} onChange={(e) => setForm({ ...form, apply_url: e.target.value })} className="mt-1" placeholder="https://…" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-navy text-bone hover:bg-lime-600 hover:text-ink" data-testid="vac-form-submit">{editing ? "Save changes" : "Post vacancy"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
